/* htmltoudf.js - Offline HTML → UDF (UYAP)
   - No external libs
   - Non-recursive sanitizer + non-recursive inline emitter (copy-paste safe)
   - Produces .udf as ZIP containing content.xml
*/
(() => {
    "use strict";
  
    const ALLOWED_TAGS = new Set([
      "P","DIV","SPAN","BR",
      "B","STRONG","I","EM","U",
      "H1","H2","H3","H4","H5","H6",
      "UL","OL","LI",
      "TABLE","TBODY","THEAD","TR","TD","TH","COLGROUP","COL",
      "BLOCKQUOTE","A","FONT"
    ]);
  
    const KEEP_STYLE_KEYS = new Set([
      "font-weight","font-style","text-decoration","text-align","font-size","font-family","width","color","background-color","text-indent","margin-left","padding-left","list-style-type"
    ]);
  
    function isBlockTag(tagName) {
      return ["P","DIV","H1","H2","H3","H4","H5","H6","UL","OL","LI","TABLE","TR","TD","TH","BLOCKQUOTE","A","FONT"].includes(tagName);
    }
  
    function stripIllegalXmlChars(s) {
      return String(s).replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, "");
    }
  
    function escapeXmlAttr(s) {
      return String(s).replace(/&/g,"&amp;").replace(/"/g,"&quot;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
    }
    function escapeCdata(s) { return String(s).replace(/\]\]>/g, "]]&gt;"); }
  
    function cleanStyle(styleText) {
      if (!styleText) return "";
      styleText = String(styleText)
        .replace(/mso-[^:]+:[^;]+;?/gi, "")
        .replace(/tab-stops:[^;]+;?/gi, "")
        .replace(/line-height:[^;]+;?/gi, "");
      const out = [];
      styleText.split(";").forEach(part => {
        const idx = part.indexOf(":");
        if (idx < 0) return;
        const k = part.slice(0, idx).trim().toLowerCase();
        const v = part.slice(idx + 1).trim();
        if (!k || !v) return;
        if (!KEEP_STYLE_KEYS.has(k)) return;
        out.push(`${k}: ${v}`);
      });
      return out.join("; ");
    }
  
    function basicClean(html) {
      return String(html || "")
        .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
        .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
        .replace(/<!--([\s\S]*?)-->/g, "");
  
      // Map legacy HTML <font size="1..7"> (and relative +/-) to CSS font-size.
      function fontSizeFromFontTag(sizeAttr) {
        const s = String(sizeAttr || "").trim();
        const map = {1:"8pt",2:"10pt",3:"12pt",4:"14pt",5:"18pt",6:"24pt",7:"36pt"};
        if (!s) return "12pt";
        if (/^[+-]\d+$/.test(s)) {
          const n = parseInt(s, 10);
          const abs = Math.max(1, Math.min(7, 3 + n)); // base=3
          return map[abs] || "12pt";
        }
        const n = parseInt(s, 10);
        if (!Number.isNaN(n) && map[n]) return map[n];
        if (/^\d+(?:\.\d+)?(px|pt)$/i.test(s)) return s;
        return "12pt";
      }
  
    }
  
    function sanitizeHtml(dirty) {
      dirty = String(dirty || "")
        .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
        .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
        .replace(/<!--([\s\S]*?)-->/g, "")
        .replace(/<!\[[\s\S]*?\]>/g, "");
  
      const doc = new DOMParser().parseFromString(`<div id="__root">${dirty}</div>`, "text/html");
      const root = doc.getElementById("__root");
      const cleanDoc = document.implementation.createHTMLDocument("");
      const cleanRoot = cleanDoc.createElement("div");
  
      function normTag(tag) {
        tag = (tag || "").toUpperCase();
        if (tag === "B") return "STRONG";
        if (tag === "EM") return "I";
        if (tag === "FONT") return "SPAN";
        if (tag === "A") return "SPAN";
        return tag;
      }
  
      const stack = [];
      for (let i = root.childNodes.length - 1; i >= 0; i--) {
        stack.push({ srcNode: root.childNodes[i], dstParent: cleanRoot, parentTag: "" });
      }
  
      while (stack.length) {
        const { srcNode: node, dstParent, parentTag } = stack.pop();
        if (!node) continue;
  
        if (node.nodeType === 3) {
          const txt = stripIllegalXmlChars(node.nodeValue || "").replace(/\u00A0/g, " ");
          dstParent.appendChild(cleanDoc.createTextNode(txt));
          continue;
        }
        if (node.nodeType !== 1) continue;
  
        const originalTag = (node.tagName || "").toUpperCase();
        let tag = normTag(originalTag);
        if (!tag) continue;
  
        if (!ALLOWED_TAGS.has(tag)) {
          for (let i = node.childNodes.length - 1; i >= 0; i--) {
            stack.push({ srcNode: node.childNodes[i], dstParent, parentTag });
          }
          continue;
        }
  
        if (tag === "P" && parentTag === "P") {
          dstParent.appendChild(cleanDoc.createElement("br"));
          for (let i = node.childNodes.length - 1; i >= 0; i--) {
            stack.push({ srcNode: node.childNodes[i], dstParent, parentTag: "P" });
          }
          continue;
        }
  
        const el = cleanDoc.createElement(tag.toLowerCase());
  
        // Preserve key inline styles, and also translate legacy <font> attributes to CSS.
        let style = cleanStyle(node.getAttribute("style"));
        if (originalTag === "FONT") {
          const adds = [];
          const color = node.getAttribute("color");
          const face  = node.getAttribute("face");
          const size  = node.getAttribute("size");
          if (color) adds.push(`color:${color}`);
          if (face)  adds.push(`font-family:${face}`);
          if (size)  adds.push(`font-size:${fontSizeFromFontTag(size)}`);
          if (adds.length) style = (style ? style + ";" : "") + adds.join(";");
        }
        style = cleanStyle(style);
        if (style) el.setAttribute("style", style);
  
  
        const align = node.getAttribute("align");
        if (align) el.setAttribute("align", align);
  
        const width = node.getAttribute("width");
        if (width) el.setAttribute("width", width);
  
        const colspan = node.getAttribute("colspan");
        if (colspan) el.setAttribute("colspan", colspan);
  
        const rowspan = node.getAttribute("rowspan");
        if (rowspan) el.setAttribute("rowspan", rowspan);
  
        const type = node.getAttribute("type");
        if (type) el.setAttribute("type", type);
  
        const start = node.getAttribute("start");
        if (start) el.setAttribute("start", start);
  
        const value = node.getAttribute("value");
        if (value) el.setAttribute("value", value);
  
        dstParent.appendChild(el);
  
        for (let i = node.childNodes.length - 1; i >= 0; i--) {
          stack.push({ srcNode: node.childNodes[i], dstParent: el, parentTag: tag });
        }
      }
  
      cleanRoot.querySelectorAll("div").forEach(div => {
        if (div.closest("table")) return;
        const hasBlock = [...div.children].some(c => isBlockTag(c.tagName.toUpperCase()));
        if (!hasBlock) {
          const p = cleanDoc.createElement("p");
          if (div.getAttribute("style")) p.setAttribute("style", div.getAttribute("style"));
          p.innerHTML = div.innerHTML;
          div.replaceWith(p);
        }
      });
  
      cleanRoot.querySelectorAll("table").forEach(tbl => {
        const hasTbody = !!tbl.querySelector("tbody");
        const directTr = [...tbl.children].some(c => c.tagName.toUpperCase() === "TR");
        if (!hasTbody && directTr) {
          const tbody = cleanDoc.createElement("tbody");
          [...tbl.children].forEach(ch => {
            if (ch.tagName.toUpperCase() === "TR") tbody.appendChild(ch);
          });
          tbl.appendChild(tbody);
        }
      });
  
      return cleanRoot.innerHTML.trim();
    }
  
    function normalizeHtml(html) {
      const src = String(html || "");
      try {
        const cleaned = sanitizeHtml(src);
        const hasVisible = src.replace(/<[^>]+>/g, "").trim().length > 0;
        if ((cleaned == null || cleaned.trim() === "") && hasVisible) return basicClean(src);
        return cleaned;
      } catch {
        return basicClean(src);
      }
    }
  
    function parseCssText(styleText) {
      const out = {};
      if (!styleText) return out;
      String(styleText).split(";").forEach(part => {
        const idx = part.indexOf(":");
        if (idx < 0) return;
        const k = part.slice(0, idx).trim().toLowerCase();
        const v = part.slice(idx + 1).trim();
        if (!k || !v) return;
        out[k] = v;
      });
      return out;
    }
  
  
    function toAlpha(n, upper) {
      // 1 -> a, 26 -> z, 27 -> aa ...
      let num = Math.max(1, n | 0);
      let s = "";
      while (num > 0) {
        num--; // 1-based
        s = String.fromCharCode(97 + (num % 26)) + s;
        num = Math.floor(num / 26);
      }
      return upper ? s.toUpperCase() : s;
    }
  
    function toRoman(n, upper) {
      let num = Math.max(1, n | 0);
      const map = [
        ["M",1000],["CM",900],["D",500],["CD",400],
        ["C",100],["XC",90],["L",50],["XL",40],
        ["X",10],["IX",9],["V",5],["IV",4],["I",1]
      ];
      let out = "";
      for (const [sym, val] of map) {
        while (num >= val) { out += sym; num -= val; }
      }
      return upper ? out : out.toLowerCase();
    }
  
    function u32(n) {
      const b = new Uint8Array(4);
      b[0] = n & 255;
      b[1] = (n >>> 8) & 255;
      b[2] = (n >>> 16) & 255;
      b[3] = (n >>> 24) & 255;
      return b;
    }
  
    const CRC_TABLE = (() => {
      const tbl = new Uint32Array(256);
      for (let i = 0; i < 256; i++) {
        let c = i;
        for (let k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
        tbl[i] = c >>> 0;
      }
      return tbl;
    })();
  
    function crc32(buf) {
      let c = 0xFFFFFFFF;
      for (let i = 0; i < buf.length; i++) c = CRC_TABLE[(c ^ buf[i]) & 0xFF] ^ (c >>> 8);
      return (c ^ 0xFFFFFFFF) >>> 0;
    }
  
    function concatBytes(...parts) {
      let len = 0;
      for (const p of parts) len += p.length;
      const out = new Uint8Array(len);
      let off = 0;
      for (const p of parts) { out.set(p, off); off += p.length; }
      return out;
    }
  
    function textBytes(s) { return new TextEncoder().encode(String(s)); }
  
    function makeZipSingleFile(filename, fileBytes) {
      const nameBytes = textBytes(filename);
      const crc = crc32(fileBytes);
      const compSize = fileBytes.length;
      const uncompSize = fileBytes.length;
  
      const localHeader = concatBytes(
        u32(0x04034b50),
        new Uint8Array([20, 0]),
        new Uint8Array([0, 0]),
        new Uint8Array([0, 0]),
        new Uint8Array([0, 0, 0, 0]),
        u32(crc),
        u32(compSize),
        u32(uncompSize),
        new Uint8Array([nameBytes.length & 255, (nameBytes.length >>> 8) & 255]),
        new Uint8Array([0, 0]),
        nameBytes
      );
  
      const fileStart = localHeader.length;
      const fileEnd = fileStart + fileBytes.length;
  
      const centralHeader = concatBytes(
        u32(0x02014b50),
        new Uint8Array([20, 0]),
        new Uint8Array([20, 0]),
        new Uint8Array([0, 0]),
        new Uint8Array([0, 0]),
        new Uint8Array([0, 0, 0, 0]),
        u32(crc),
        u32(compSize),
        u32(uncompSize),
        new Uint8Array([nameBytes.length & 255, (nameBytes.length >>> 8) & 255]),
        new Uint8Array([0, 0]),
        new Uint8Array([0, 0]),
        new Uint8Array([0, 0]),
        new Uint8Array([0, 0]),
        u32(0),
        u32(0),
        nameBytes
      );
  
      const centralStart = fileEnd;
  
      const end = concatBytes(
        u32(0x06054b50),
        new Uint8Array([0, 0, 0, 0]),
        new Uint8Array([1, 0, 1, 0]),
        u32(centralHeader.length),
        u32(centralStart),
        new Uint8Array([0, 0])
      );
  
      const zipBytes = concatBytes(localHeader, fileBytes, centralHeader, end);
      return new Blob([zipBytes], { type: "application/octet-stream" });
    }
  
    class UdfBuilder {
      constructor(opts = {}) {
        this.content = "";
        this.elements = "";
        this.offset = 0;
  
        this.defaultFamily = opts.defaultFamily || "Times New Roman";
        this.defaultSize = String(opts.defaultSize || "12");
  
        this.leftMargin = opts.leftMargin || "42.525";
        this.rightMargin = opts.rightMargin || "42.525";
        this.topMargin = opts.topMargin || "42.525";
        this.bottomMargin = opts.bottomMargin || "42.525";
  
        this.uiFamily = opts.uiFamily || "Tahoma";
        this.uiSize = String(opts.uiSize || "11");

        // When true, top-level borderless "layout" tables (e.g. the Takip Talebi
        // form) are emitted as one table PER ROW instead of a single monolithic
        // table. UYAP's editor keeps a single multi-page table together and pushes
        // it to page 2 (leaving page 1 blank); per-row tables let it flow and fill
        // the first page. Scoped via option so other documents are unaffected.
        this.splitLayoutTables = !!opts.splitLayoutTables;
      }
  
      reset() { this.content = ""; this.elements = ""; this.offset = 0; }
  
      // --- low-level content management ---
      addText(text) {
        if (text == null) return 0;
        text = String(text);
        if (!text) return 0;
        this.content += text;
        this.offset += text.length;
        return text.length;
      }
  
      addNewline() { return this.addText("\n"); }
  
      // Compares only keys that affect output attributes
      styleKey(style) {
        return [
          style.family || this.defaultFamily,
          style.size || this.defaultSize,
          style.bold ? "b1" : "b0",
          style.italic ? "i1" : "i0",
          style.underline ? "u1" : "u0",
          (style.foreground != null ? String(style.foreground) : ""),
          (style.align != null ? String(style.align) : ""),
        ].join("|");
      }
  
      pushRun(runs, style, startOffset, length) {
        if (length <= 0) return;
        const key = this.styleKey(style);
        const last = runs.length ? runs[runs.length - 1] : null;
        if (last && last.key === key && (last.start + last.length) === startOffset) {
          last.length += length;
          return;
        }
        runs.push({ key, style: { ...style }, start: startOffset, length });
      }
  
      addTextRun(text, style, runs) {
        const start = this.offset;
        const len = this.addText(text);
        this.pushRun(runs, style, start, len);
      }
  
      // --- style parsing / merging ---
      // UYAP uses Java signed int colors (ARGB). We store foreground as signed int string.
      cssColorToSignedInt(color) {
        if (!color) return null;
        let c = String(color).trim().toLowerCase();
        if (!c) return null;
  
        const named = {
          black: "#000000", white: "#ffffff", red: "#ff0000", green: "#008000",
          blue: "#0000ff", yellow: "#ffff00", gray: "#808080", grey: "#808080",
          silver: "#c0c0c0", maroon: "#800000", olive: "#808000", lime: "#00ff00",
          navy: "#000080", teal: "#008080", fuchsia: "#ff00ff", purple: "#800080",
          orange: "#ffa500"
        };
        if (named[c]) c = named[c];
  
        const hex3 = /^#([0-9a-f]{3})$/i;
        const hex6 = /^#([0-9a-f]{6})$/i;
        const rgb = /^rgb\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*\)$/i;
        const rgba = /^rgba\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]*\.?[0-9]+)\s*\)$/i;
  
        let r=null,g=null,b=null,a=255;
  
        let m;
        if ((m = c.match(hex3))) {
          const h = m[1];
          r = parseInt(h[0] + h[0], 16);
          g = parseInt(h[1] + h[1], 16);
          b = parseInt(h[2] + h[2], 16);
        } else if ((m = c.match(hex6))) {
          const h = m[1];
          r = parseInt(h.slice(0,2), 16);
          g = parseInt(h.slice(2,4), 16);
          b = parseInt(h.slice(4,6), 16);
        } else if ((m = c.match(rgb))) {
          r = Math.min(255, parseInt(m[1],10));
          g = Math.min(255, parseInt(m[2],10));
          b = Math.min(255, parseInt(m[3],10));
        } else if ((m = c.match(rgba))) {
          r = Math.min(255, parseInt(m[1],10));
          g = Math.min(255, parseInt(m[2],10));
          b = Math.min(255, parseInt(m[3],10));
          const af = Math.max(0, Math.min(1, parseFloat(m[4])));
          a = Math.round(af * 255);
        } else {
          return null;
        }
  
        const u32 = ((a & 255) << 24) | ((r & 255) << 16) | ((g & 255) << 8) | (b & 255);
        // Convert to signed int32 as string
        return String(u32 | 0);
      }
  
      mergeStyle(parent, node) {
        const s = { ...parent };
        if (!node || node.nodeType !== 1) return s;
  
        const tag = (node.tagName || "").toUpperCase();
        const css = parseCssText(node.getAttribute("style") || "");
  
        if (css["font-family"]) s.family = css["font-family"].split(",")[0].trim().replace(/^["']|["']$/g,"");
  
        if (css["font-size"]) {
          const fsRaw = String(css["font-size"]).trim().toLowerCase();
          const map = {"xx-small":8,"x-small":10,"small":12,"medium":14,"large":18,"x-large":24,"xx-large":36};
          let pt = null;
          if (map[fsRaw] != null) pt = map[fsRaw];
          else if (fsRaw.endsWith("pt")) pt = parseFloat(fsRaw);
          else if (fsRaw.endsWith("px")) pt = Math.round((parseFloat(fsRaw) * 0.75) * 100) / 100; // px→pt
          else { const n = parseFloat(fsRaw); if (!Number.isNaN(n)) pt = n; }
          if (pt != null && !Number.isNaN(pt)) s.size = String(Math.round(pt));
        }
  
        const fwRaw = (css["font-weight"] || "");
        const fw = String(fwRaw).trim().toLowerCase();
  
        // Tag semantics (can be overridden by CSS)
        if (tag === "STRONG" || tag === "B") s.bold = true;
  
        if (fw) {
          if (fw === "normal" || fw === "lighter") s.bold = false;
          else if (fw === "bold" || fw === "bolder") s.bold = true;
          else {
            const fwn = parseInt(fw, 10);
            if (!Number.isNaN(fwn)) s.bold = (fwn >= 500);
          }
        }
  
        const fsRaw = (css["font-style"] || "");
        const fs = String(fsRaw).trim().toLowerCase();
        if (tag === "I" || tag === "EM") s.italic = true;
        if (fs) {
          if (fs === "normal") s.italic = false;
          else if (fs === "italic" || fs === "oblique") s.italic = true;
        }
  
        const tdRaw = (css["text-decoration"] || "");
        const td = String(tdRaw).trim().toLowerCase();
        if (tag === "U") s.underline = true;
        if (td) {
          if (td === "none") s.underline = false;
          else if (td.includes("underline")) s.underline = true;
        }
  
        const color = css["color"] || node.getAttribute("color");
        const fg = this.cssColorToSignedInt(color);
        if (fg != null) s.foreground = fg;
  
        // Alignment (paragraph-level; still safe to carry into content)
        const al = (css["text-align"] || node.getAttribute("align") || "").toLowerCase();
        if (al === "center") s.align = 1;
        else if (al === "right") s.align = 2;
        else if (al === "justify") s.align = 3;
        else if (al === "left") s.align = 0;
  
        return s;
      }
  
      // Builds attributes for a <content .../> tag
      styleAttrs(style) {
        const a = [];
        const size = style.size || this.defaultSize;
        const family = style.family || this.defaultFamily;
  
        a.push(`family="${escapeXmlAttr(family)}"`);
        a.push(`size="${escapeXmlAttr(size)}"`);
  
        if (style.bold) a.push(`bold="true"`);
        if (style.italic) a.push(`italic="true"`);
        if (style.underline) a.push(`underline="true"`);
  
        if (style.foreground != null) a.push(`foreground="${escapeXmlAttr(String(style.foreground))}"`);
        if (style.align != null) a.push(`Alignment="${escapeXmlAttr(String(style.align))}"`);
  
        // Keep resolver only when present explicitly; sample UDFs often include it
        a.push(`resolver="hvl-default"`);
        return a.join(" ");
      }
  
      emitContentTag(style, startOffset, length) {
        if (length <= 0) return "";
        const attrs = `${this.styleAttrs(style)} startOffset="${startOffset}" length="${length}"`;
        return `<content ${attrs} />`;
      }
  
      // --- inline run collection ---
      // Walks inline descendants in DOM order and emits text runs.
      collectInlineRuns(node, baseStyle, runs, opts = {}) {
        const stopAtLists = !!opts.stopAtLists;
        const stopAtTables = !!opts.stopAtTables;
  
        const walk = (n, inherited) => {
          if (!n) return;
          if (n.nodeType === 3) {
            let txt = n.nodeValue || "";
            // keep spaces, normalize newlines
            txt = stripIllegalXmlChars(txt).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
            // keep nbsp but convert to normal space (still counted)
            txt = txt.replace(/\u00A0/g, " ");
            // remove zero-widths
            txt = txt.replace(/[\u200B-\u200D\uFEFF]/g, "");
            if (txt) this.addTextRun(txt, inherited, runs);
            return;
          }
          if (n.nodeType !== 1) return;
  
          const tag = (n.tagName || "").toUpperCase();
          if (tag === "BR") { this.addTextRun("\n", inherited, runs); return; }
          if (stopAtTables && tag === "TABLE") return;
          if (stopAtLists && (tag === "UL" || tag === "OL")) return;
  
          const next = this.mergeStyle(inherited, n);
          const kids = n.childNodes;
          for (let i = 0; i < kids.length; i++) walk(kids[i], next);
        };
  
        walk(node, baseStyle);
      }
  
      // --- block handlers ---
      paragraphFrom(node, baseStyle, paragraphOpts = {}) {
        const style = this.mergeStyle(baseStyle, node);
        const align = (style.align ?? 0);
  
        const start = this.offset;
        const runs = [];
  
        // optional prefix (used for lists)
        if (paragraphOpts.prefix) {
          this.addTextRun(String(paragraphOpts.prefix), style, runs);
        }
  
        this.collectInlineRuns(node, style, runs, paragraphOpts.inlineOpts || {});
        // paragraph terminator
        this.addTextRun("\n", style, runs);
  
        const len = this.offset - start;
        if (len <= 0) return "";
  
        const leftIndent = paragraphOpts.leftIndent != null ? paragraphOpts.leftIndent : "1.0";
        const rightIndent = paragraphOpts.rightIndent != null ? paragraphOpts.rightIndent : "1.0";
  
        const extraAttrs = paragraphOpts.extraAttrs ? (" " + paragraphOpts.extraAttrs.trim()) : "";
        const runXml = runs.map(r => this.emitContentTag(r.style, r.start, r.length)).join("");
  
        return `<paragraph Alignment="${align}" resolver="hvl-default" LeftIndent="${escapeXmlAttr(String(leftIndent))}" RightIndent="${escapeXmlAttr(String(rightIndent))}"${extraAttrs}>${runXml}</paragraph>`;
      }
  
      containsBlockChildren(el) {
        if (!el || el.nodeType !== 1) return false;
        const kids = el.children || [];
        for (let i = 0; i < kids.length; i++) {
          const t = (kids[i].tagName || "").toUpperCase();
          if (t === "TABLE" || t === "UL" || t === "OL" || t === "P" || t === "DIV" || t.startsWith("H") || t === "BLOCKQUOTE" || t === "A" || t === "FONT") return true;
        }
        return false;
      }
  
      handleList(listEl, baseStyle, level = 1) {
        const tag = ((listEl.tagName || "").toUpperCase());
        const ordered = (tag === "OL");
  
        const listCss = parseCssText(listEl.getAttribute("style") || "");
        const typeAttr = listEl.getAttribute("type");
        let markerType = String(listCss["list-style-type"] || "").trim().toLowerCase();
  
        // HTML ol[type] fallback (some editors prefer this)
        if (!markerType && typeAttr) {
          if (typeAttr === "a") markerType = "lower-alpha";
          else if (typeAttr === "A") markerType = "upper-alpha";
          else if (typeAttr === "i") markerType = "lower-roman";
          else if (typeAttr === "I") markerType = "upper-roman";
          else markerType = "decimal";
        }
        if (!markerType) markerType = "decimal";
  
        // start index (default 1)
        const startAttr = parseInt(listEl.getAttribute("start") || "1", 10);
        const start = (Number.isFinite(startAttr) && startAttr > 0) ? startAttr : 1;
  
        const items = listEl.querySelectorAll(":scope > li");
        let idx = 0;
  
        items.forEach(li => {
          idx++;
          const liStyle = this.mergeStyle(baseStyle, li);
  
          // li[value] can override numbering for a single item
          const valueAttr = parseInt(li.getAttribute("value") || "", 10);
          const n = (Number.isFinite(valueAttr) && valueAttr > 0) ? valueAttr : (start + idx - 1);
  
          let prefix = "";
          if (ordered) {
            if (markerType === "lower-alpha" || markerType === "lower-latin") prefix = `${toAlpha(n, false)}. `;
            else if (markerType === "upper-alpha" || markerType === "upper-latin") prefix = `${toAlpha(n, true)}. `;
            else if (markerType === "lower-roman") prefix = `${toRoman(n, false)}. `;
            else if (markerType === "upper-roman") prefix = `${toRoman(n, true)}. `;
            else prefix = `${n}. `;
          }
  
          const leftIndent = 25 + (level - 1) * 18;
  
          const extraAttrs = ordered
            ? ``
            : `Bulleted="true" BulletType="BULLET_TYPE_ELLIPSE" ListLevel="${level}" ListId="1"`;
  
          // Stop inline walk at nested lists/tables so they become separate blocks after this paragraph
          this.elements += this.paragraphFrom(li, liStyle, {
            prefix,
            leftIndent: String(leftIndent),
            rightIndent: "1.0",
            extraAttrs,
            inlineOpts: { stopAtLists: true, stopAtTables: true }
          });
  
          // nested lists inside this li
          const nested = li.querySelectorAll(":scope > ul, :scope > ol");
          nested.forEach(nl => this.handleList(nl, liStyle, level + 1));
        });
  
        // Add a spacer paragraph between block elements (matches HTML default margins).
        // Avoid injecting this spacer for nested lists within an <li>.
        const parentTag = ((listEl.parentElement && listEl.parentElement.tagName) || "").toUpperCase();
        if (parentTag !== "LI") {
          const spacer = document.createElement("span");
          this.elements += this.paragraphFrom(spacer, baseStyle, { prefix: " ", leftIndent: "0.0", rightIndent: "1.0" });
        }
      }
  
      findColumnCount(table) {
        const tb = table.querySelector("tbody") || table;
        const trs = tb.children;
        let maxCols = 0;
        let widths = [];
  
        for (let r = 0; r < trs.length; r++) {
          const row = trs[r];
          if (!row || (row.tagName || "").toUpperCase() !== "TR") continue;
          const tds = row.children;
          let cols = 0;
          const w = [];
          for (let c = 0; c < tds.length; c++) {
            const cell = tds[c];
            const ct = (cell.tagName || "").toUpperCase();
            if (ct !== "TD" && ct !== "TH") continue;
            cols++;
            const style = parseCssText(cell.getAttribute("style") || "");
            const sw = style["width"] || cell.getAttribute("width");
            if (sw) w.push(String(sw).replace(/px|%/g, "").trim());
          }
          if (cols > maxCols) { maxCols = cols; widths = w; }
        }
  
        if (!maxCols) maxCols = 1;
        if (!widths || widths.length !== maxCols) widths = Array.from({ length: maxCols }, () => "100");
  
        const total = widths.reduce((s, a) => s + (parseFloat(a) || 0), 0) || (100 * maxCols);
        const spans = widths.map(v => String(parseInt(100 * (parseFloat(v) || 0) / total * maxCols, 10))).join(",");
        return { count: maxCols, columnSpans: spans };
      }
  
      handleTable(table, baseStyle, isTopLevel = false) {
        const col = this.findColumnCount(table);
        // Split a top-level layout table into one table PER ROW (see splitLayoutTables).
        const split = isTopLevel && this.splitLayoutTables;
        const tableOpen = split
          ? `<table tableName="Sabit" columnCount="${col.count}" columnSpans="${escapeXmlAttr(col.columnSpans)}" border="borderNone">`
          : `<table tableName="Sabit" columnCount="${col.count}" columnSpans="${escapeXmlAttr(col.columnSpans)}" border="borderCell">`;
        if (!split) this.elements += tableOpen;
  
        const tbody = table.querySelector("tbody") || table;
        const rows = tbody.querySelectorAll(":scope > tr");
        let rowIndex = 0;
  
        rows.forEach(tr => {
          rowIndex++;
          if (split) this.elements += tableOpen;
          this.elements += `<row rowName="row${rowIndex}" rowType="dataRow" height="0.0" border="borderNone" borderWidth="0.5">`;
  
          const cells = tr.querySelectorAll(":scope > td, :scope > th");
          cells.forEach(td => {
            const cellStyle = this.mergeStyle(baseStyle, td);
            this.elements += `<cell>`;

            const nestedTable = td.querySelector(":scope > table");
            if (nestedTable) {
              this.handleTable(nestedTable, cellStyle);
              this.elements += `</cell>`;
              return;
            }

            const childBlocks = Array.from(td.children || []).filter(el => {
              const t = (el.tagName || "").toUpperCase();
              return t === "P" || t === "DIV" || t === "BLOCKQUOTE" || t === "A" || t === "FONT" || t.startsWith("H") || t === "UL" || t === "OL" || t === "TABLE";
            });
  
            if (childBlocks.length) {
              childBlocks.forEach(b => {
                const t = (b.tagName || "").toUpperCase();
                if (t === "TABLE") this.handleTable(b, cellStyle);
                else if (t === "UL" || t === "OL") this.handleList(b, cellStyle, 1);
                else this.elements += this.paragraphFrom(b, cellStyle);
              });
            } else {
              this.elements += this.paragraphFrom(td, cellStyle);
            }
  
            this.elements += `</cell>`;
          });
  
          this.elements += `</row>`;
          if (split) this.elements += `</table>`;
        });

        if (!split) this.elements += `</table>`;

        // spacer line after table (skipped when split: the form's own &nbsp; spacer
        // rows already provide vertical spacing; one spacer per row would accumulate).
        if (!split) {
          this.elements += this.paragraphFrom(document.createElement("span"), baseStyle, {
            prefix: "",
            leftIndent: "0.0",
            rightIndent: "0.0",
            inlineOpts: {},
          });
        }
      }
  
      handleBlock(node, baseStyle) {
        if (!node) return;
        if (node.nodeType === 3) {
          const txt = (node.nodeValue || "").trim();
          if (!txt) return;
          const p = document.createElement("p");
          p.textContent = txt;
          this.elements += this.paragraphFrom(p, baseStyle);
          return;
        }
        if (node.nodeType !== 1) return;
  
        const tag = (node.tagName || "").toUpperCase();
        if (tag === "TABLE") { this.handleTable(node, baseStyle, true); return; }
        if (tag === "UL" || tag === "OL") { this.handleList(node, baseStyle, 1); return; }
  
        if (tag === "DIV" || tag === "BLOCKQUOTE" || tag === "A" || tag === "FONT") {
          if (this.containsBlockChildren(node)) {
            Array.from(node.childNodes).forEach(ch => this.handleBlock(ch, baseStyle));
            return;
          }
          this.elements += this.paragraphFrom(node, baseStyle);
          return;
        }
  
        if (tag === "P" || tag.startsWith("H")) {
          this.elements += this.paragraphFrom(node, baseStyle);
          return;
        }
  
        // default: drill down
        Array.from(node.childNodes).forEach(ch => this.handleBlock(ch, baseStyle));
      }
  
      convert(normalizedHtml) {
        this.reset();
  
        const dom = document.createElement("div");
        dom.innerHTML = `<root>${normalizedHtml}</root>`;
        const root = dom.querySelector("root");
  
        const baseStyle = {
          family: this.defaultFamily,
          size: this.defaultSize,
          align: 0,
          bold: false,
          italic: false,
          underline: false
        };
  
        Array.from(root.childNodes).forEach(n => {
          if (n.nodeType === 3) {
            const txt = (n.nodeValue || "").trim();
            if (!txt) return;
            const p = document.createElement("p");
            p.textContent = txt;
            this.elements += this.paragraphFrom(p, baseStyle);
            return;
          }
          this.handleBlock(n, baseStyle);
        });
  
        // final terminator paragraph (UYAP tolerates; some editors expect a trailing paragraph)
        const start = this.offset;
        this.addNewline();
        const len = this.offset - start;
        this.elements += `<paragraph resolver="hvl-default" LeftIndent="0.0" RightIndent="0.0">${this.emitContentTag(baseStyle, start, len)}</paragraph>`;
  
        const contentCdata = `<content><![CDATA[${escapeCdata(this.content)}]]></content>`;
  
        return `<?xml version="1.0" encoding="UTF-8" ?>\n` +
          `<template format_id="1.7">\n` +
          `${contentCdata}\n` +
          `<properties><pageFormat mediaSizeName="1" leftMargin="${escapeXmlAttr(this.leftMargin)}" rightMargin="${escapeXmlAttr(this.rightMargin)}" topMargin="${escapeXmlAttr(this.topMargin)}" bottomMargin="${escapeXmlAttr(this.bottomMargin)}" paperOrientation="1" headerFOffset="20.0" footerFOffset="20.0" /></properties>\n` +
          `<elements>\n${this.elements}\n</elements>\n` +
          `<styles>\n` +
          `  <style name="default" description="Geçerli" italic="false" bold="false" FONT_ATTRIBUTE_KEY="javax.swing.plaf.FontUIResource[family=${escapeXmlAttr(this.uiFamily)},name=${escapeXmlAttr(this.uiFamily)},style=plain,size=${escapeXmlAttr(this.uiSize)}]" size="${escapeXmlAttr(this.uiSize)}" family="${escapeXmlAttr(this.uiFamily)}" />\n` +
          `  <style name="hvl-default" description="Gövde" size="${escapeXmlAttr(this.defaultSize)}" family="${escapeXmlAttr(this.defaultFamily)}" />\n` +
          `</styles>\n` +
          `</template>\n`;
      }
    }
  
    // --- UDF (content.xml) -> HTML preview renderer ---------------------
    // Renders what UYAP Editor actually shows: it reads the top <content>
    // CDATA as the source text and the <elements> tree (paragraph/table/cell
    // + per-run <content startOffset length .../>) as the formatting layer.
    // This is the round-trip view, so any conversion loss (e.g. content that
    // html2udf dropped) is visible BEFORE the user downloads.
    function renderUdfXmlToHtml(xml) {
      if (!xml) return "";
      const doc = new DOMParser().parseFromString(String(xml), "application/xml");
      if (doc.querySelector("parsererror")) {
        // Fallback: show raw text if XML is malformed
        const m = String(xml).match(/<!\[CDATA\[([\s\S]*?)\]\]>/i);
        return `<pre>${escapeXmlAttr(m ? m[1] : xml)}</pre>`;
      }

      const tpl = doc.querySelector("template") || doc.documentElement;

      // Source text lives in the FIRST top-level <content> (the CDATA block).
      let sourceText = "";
      const topContents = Array.from(tpl.children || []).filter(
        (c) => c.tagName && c.tagName.toLowerCase() === "content"
      );
      if (topContents.length) sourceText = topContents[0].textContent || "";

      const elements = tpl.querySelector("elements");
      if (!elements) return `<pre>${escapeXmlAttr(sourceText)}</pre>`;

      const slice = (off, len) => {
        const start = parseInt(off, 10);
        const length = parseInt(len, 10);
        if (Number.isNaN(start) || Number.isNaN(length)) return "";
        return sourceText.substring(start, start + length);
      };

      const escHtml = (s) =>
        String(s == null ? "" : s)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");

      // One <content .../> run -> styled inline span. Newlines become <br>.
      const renderRun = (cNode) => {
        const off = cNode.getAttribute("startOffset");
        const len = cNode.getAttribute("length");
        let text = slice(off, len);
        const styles = [];
        const family = cNode.getAttribute("family");
        const size = cNode.getAttribute("size");
        if (family) styles.push(`font-family:${family}`);
        if (size) styles.push(`font-size:${size}pt`);
        if (cNode.getAttribute("bold") === "true") styles.push("font-weight:bold");
        if (cNode.getAttribute("italic") === "true") styles.push("font-style:italic");
        if (cNode.getAttribute("underline") === "true") styles.push("text-decoration:underline");
        const fg = cNode.getAttribute("foreground");
        if (fg != null && fg !== "") {
          const c = signedIntToCssColor(fg);
          if (c) styles.push(`color:${c}`);
        }
        const html = escHtml(text).replace(/\n/g, "<br>");
        return `<span style="${styles.join(";")}">${html}</span>`;
      };

      const alignFromAttr = (a) => {
        switch (String(a)) {
          case "1": return "center";
          case "2": return "right";
          case "3": return "justify";
          default: return "left";
        }
      };

      const renderParagraph = (pNode) => {
        const align = alignFromAttr(pNode.getAttribute("Alignment"));
        const runs = Array.from(pNode.children || []).filter(
          (c) => c.tagName && c.tagName.toLowerCase() === "content"
        );
        const inner = runs.map(renderRun).join("");
        return `<p style="margin:0;text-align:${align};white-space:pre-wrap">${inner || "&nbsp;"}</p>`;
      };

      const renderCell = (cellNode, colSpan) => {
        const blocks = Array.from(cellNode.children || []);
        const inner = blocks.map(renderNode).join("");
        const span = colSpan && colSpan > 1 ? ` colspan="${colSpan}"` : "";
        // overflow-wrap kelimeleri kırar ama ayraç ('______') gibi tek uzun
        // "kelimeyi" de kırardı; word-break:keep-all bunu engeller.
        return `<td${span} style="border:none;vertical-align:top;padding:1px 6px;overflow-wrap:break-word;word-break:keep-all">${inner}</td>`;
      };

      // colCount = tablonun toplam sütun sayısı. UDF'te bir satırda hücre sayısı
      // sütun sayısından azsa, SON hücre kalan sütunları kaplar (UYAP davranışı)
      // -> ayraç/toplam gibi tek-hücreli satırlar tam genişlik alır, sıkışmaz.
      const renderRow = (rowNode, colCount) => {
        const cells = Array.from(rowNode.children || []).filter(
          (c) => c.tagName && c.tagName.toLowerCase() === "cell"
        );
        return `<tr>${cells
          .map((cell, i) => {
            const isLast = i === cells.length - 1;
            const span = isLast && colCount > cells.length
              ? colCount - cells.length + 1
              : 1;
            return renderCell(cell, span);
          })
          .join("")}</tr>`;
      };

      // Build a <colgroup> from the UDF columnSpans attribute so cells get the
      // same proportional widths UYAP Editor uses (otherwise the browser
      // shrinks the first column and the text wraps one word per line).
      const parseSpans = (tNode) => {
        const spansAttr = tNode.getAttribute("columnSpans") || "";
        return spansAttr
          .split(",")
          .map((s) => parseFloat(s))
          .filter((n) => !Number.isNaN(n) && n > 0);
      };

      const buildColgroup = (spans) => {
        if (!spans.length) return "";
        const total = spans.reduce((a, b) => a + b, 0) || 1;
        const cols = spans
          .map((s) => `<col style="width:${((s / total) * 100).toFixed(2)}%" />`)
          .join("");
        return `<colgroup>${cols}</colgroup>`;
      };

      const renderTable = (tNode) => {
        const rows = Array.from(tNode.children || []).filter(
          (c) => c.tagName && c.tagName.toLowerCase() === "row"
        );
        const spans = parseSpans(tNode);
        const colCount = parseInt(tNode.getAttribute("columnCount"), 10) || spans.length || 1;
        const colgroup = buildColgroup(spans);
        return `<table style="border-collapse:collapse;width:100%;table-layout:fixed">${colgroup}${rows
          .map((r) => renderRow(r, colCount))
          .join("")}</table>`;
      };

      function renderNode(node) {
        if (!node || !node.tagName) return "";
        const tag = node.tagName.toLowerCase();
        if (tag === "paragraph") return renderParagraph(node);
        if (tag === "table") return renderTable(node);
        if (tag === "row") return renderRow(node, 0);
        if (tag === "cell") return renderCell(node, 1);
        // unknown wrapper: drill into children
        return Array.from(node.children || []).map(renderNode).join("");
      }

      const body = Array.from(elements.children || []).map(renderNode).join("");
      return `<div class="udf-preview" style="font-family:'Times New Roman',serif;font-size:12pt;color:#000">${body}</div>`;
    }

    // Java signed-int ARGB (as stored by html2udf) -> CSS hex color.
    function signedIntToCssColor(signed) {
      const n = parseInt(signed, 10);
      if (Number.isNaN(n)) return null;
      const u = n >>> 0;
      const r = (u >> 16) & 255;
      const g = (u >> 8) & 255;
      const b = u & 255;
      const hex = (v) => v.toString(16).padStart(2, "0");
      return `#${hex(r)}${hex(g)}${hex(b)}`;
    }

    class htmlToUdf {
      constructor(opts = {}) {
        this.builder = new UdfBuilder({ defaultFamily: opts.defaultFamily || "Times New Roman", defaultSize: String(opts.defaultSize || 12), splitLayoutTables: !!opts.splitLayoutTables });
      }
      destructor(){ this.builder.reset(); }
      normalize(html){ return normalizeHtml(html); }
      getUdfXml(html){ const clean = normalizeHtml(html); return this.builder.convert(clean); }
      // Returns an HTML string that mirrors how UYAP Editor will display the
      // generated UDF (round-trip preview). Pass raw component HTML.
      getUdfPreviewHtml(html){ const xml = this.getUdfXml(html); return renderUdfXmlToHtml(xml); }
      getUdfBlob(html){ const xml = this.getUdfXml(html); const bytes = new TextEncoder().encode(xml); return makeZipSingleFile("content.xml", bytes); }
      downloadUdf(html, fileName="dilekce"){
        const blob = this.getUdfBlob(html);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `${fileName}.udf`;
        document.body.appendChild(a);
        try { a.click(); } catch {}
        a.remove();
        setTimeout(()=>{ try{ URL.revokeObjectURL(url);}catch{} }, 3000);
        return blob;
      }
    }
  
    window.htmlToUdf = htmlToUdf;
    window.__udf_internal = { normalizeHtml, makeZipSingleFile, renderUdfXmlToHtml };
  })();
  
  // --- added: parseZipEntries for Self-Test (kütüphanesiz ZIP) ---
  (() => {
    "use strict";
    async function parseZipEntries(blob) {
      const buf = await blob.arrayBuffer();
      const dv = new DataView(buf);
      const u8 = new Uint8Array(buf);
      const entries = [];
      let off = 0;
  
      // Local file header signature (PK\x03\x04) => 0x04034b50 little-endian
      while (off + 30 <= dv.byteLength) {
        const sig = dv.getUint32(off, true);
        if (sig !== 0x04034b50) break;
  
        const nameLen = dv.getUint16(off + 26, true);
        const extraLen = dv.getUint16(off + 28, true);
        const compSize = dv.getUint32(off + 18, true);
  
        const nameStart = off + 30;
        const nameEnd = nameStart + nameLen;
        if (nameEnd > dv.byteLength) break;
  
        const name = new TextDecoder().decode(u8.slice(nameStart, nameEnd));
        entries.push(name);
  
        const dataStart = nameEnd + extraLen;
        const next = dataStart + compSize;
        if (next < off) break;
        off = next;
      }
      return entries;
    }
  
    window.parseZipEntries = parseZipEntries;
    window.__udf_internal = window.__udf_internal || {};
    window.__udf_internal.parseZipEntries = parseZipEntries;
  })();
  
  /* app.js — UI wiring + self-test */
  (() => {
    if (!document.getElementById('btnDownloadUdf')) return; // allow using converter in other pages
  
    "use strict";
    const $ = (id) => document.getElementById(id);
  
    const jsBadge = $("jsBadge");
    const tabVisual = $("tabVisual");
    const tabHtml = $("tabHtml");
    const tabText = $("tabText");
    const visualPane = $("visualPane");
    const htmlPane = $("htmlPane");
    const textPane = $("textPane");
    const visualEditor = $("visualEditor");
    const htmlEditor = $("htmlEditor");
    const textEditor = $("textEditor");
    const htmlRender = $("htmlRender");
    const btnPreviewToHtml = $("btnPreviewToHtml");
    const preview = $("preview");
    const xmlOut = $("xmlOut");
  
    const btnApplyHtmlToVisual = $("btnApplyHtmlToVisual");
    const btnVisualToHtml = $("btnVisualToHtml");
    const btnApplyTextToVisual = $("btnApplyTextToVisual");
    const btnVisualToText = $("btnVisualToText");
  
    const btnNormalize = $("btnNormalize");
    const btnDownloadUdf = $("btnDownloadUdf");
    const btnDownloadXml = $("btnDownloadXml");
    const btnOpenBlob = $("btnOpenBlob");
    const btnSelfTest = $("btnSelfTest");
  
    const chkPastePlain = $("chkPastePlain");
    const fileName = $("fileName");
    const defaultFamily = $("defaultFamily");
    const defaultSize = $("defaultSize");
  
    const statusDot = $("statusDot");
    const statusText = $("statusText");
    const errOut = $("errOut");
    const btnClearErr = $("btnClearErr");
  
    let lastUdfBlobUrl = null;
    let lastUdfBlob = null;
  
    function setBadge(kind, text) {
      jsBadge.classList.remove("warn","ok","bad");
      jsBadge.classList.add(kind);
      jsBadge.textContent = text;
    }
  
    function setStatus(kind, msg) {
      statusText.textContent = msg;
      statusDot.classList.remove("ok","bad","warn");
      statusDot.classList.add(kind);
    }
  
    function logError(err) {
      const msg = (err && (err.stack || err.message || String(err))) || "Unknown error";
      errOut.textContent = `[${new Date().toLocaleString()}]\n${msg}\n\n` + errOut.textContent;
    }
  
    window.addEventListener("error", (e) => logError(e.error || e.message));
    window.addEventListener("unhandledrejection", (e) => logError(e.reason));
    btnClearErr.addEventListener("click", () => errOut.textContent = "");
  
    function activate(tab) {
      const isVisual = (tab === "visual");
      const isHtml = (tab === "html");
      const isText = (tab === "text");
  
      tabVisual.classList.toggle("active", isVisual);
      tabHtml.classList.toggle("active", isHtml);
      if (tabText) tabText.classList.toggle("active", isText);
  
      visualPane.classList.toggle("active", isVisual);
      htmlPane.classList.toggle("active", isHtml);
      if (textPane) textPane.classList.toggle("active", isText);
  
      if (isHtml && htmlRender) {
        try {
          const converter = new window.htmlToUdf({
            defaultFamily: defaultFamily.value || "Times New Roman",
            defaultSize: Number(defaultSize.value || 12),
          });
          htmlRender.innerHTML = converter.normalize(htmlEditor.value || "");
        } catch {}
      }
    }
    tabVisual.addEventListener("click", () => activate("visual"));
    tabHtml.addEventListener("click", () => activate("html"));
    if (tabText) tabText.addEventListener("click", () => activate("text"));
  
    function escapeHtml(s){
      return String(s||"").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
    }
  
    function textToHtml(text){
      text = String(text||"").replace(/\r\n/g,"\n").replace(/\r/g,"\n");
      const parts = text.split(/\n\s*\n/);
      const paras = parts.map(p => p.split("\n").map(line => escapeHtml(line)).join("<br>"));
      return paras.map(p => `<p>${p}</p>`).join("\n");
    }
  
    function htmlToText(html){
      const d = document.createElement("div");
      d.innerHTML = html || "";
      let t = (d.innerText || d.textContent || "").replace(/\r\n/g,"\n").replace(/\r/g,"\n");
      t = t.replace(/\n{3,}/g,"\n\n").trim();
      return t;
    }
  
    function currentSourceHtml() {
      if (tabHtml.classList.contains("active")) return htmlEditor.value || "";
      if (tabText && tabText.classList.contains("active")) return textToHtml(textEditor.value || "");
      return visualEditor.innerHTML || "";
    }
  
    function setEditorsFromNormalized(normalized) {
      htmlEditor.value = normalized;
      visualEditor.innerHTML = normalized || "";
      if (htmlRender) htmlRender.innerHTML = normalized || "";
    }
  
    function buildPreviews() {
      try {
        const src = currentSourceHtml();
        const converter = new window.htmlToUdf({
          defaultFamily: defaultFamily.value || "Times New Roman",
          defaultSize: Number(defaultSize.value || 12),
        });
  
        const clean = converter.normalize(src);
        const xml = converter.getUdfXml(clean);
  
        preview.innerHTML = clean;
        if (htmlRender) htmlRender.innerHTML = clean;
        xmlOut.textContent = xml;
  
        setStatus("ok", "Önizleme güncellendi.");
        return { converter, clean, xml };
      } catch (err) {
        console.error(err);
        logError(err);
        setStatus("bad", "Dönüşüm hatası: " + (err?.message || err));
        return null;
      }
    }
  
    function downloadBlob(blob, name) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      try { a.click(); } catch {}
      a.remove();
      setTimeout(() => { try { URL.revokeObjectURL(url); } catch {} }, 3000);
      return url;
    }
  
    btnNormalize.addEventListener("click", () => {
      const res = buildPreviews();
      if (!res) return;
      setEditorsFromNormalized(res.clean);
      setStatus("ok", "Normalize edildi ve editör güncellendi.");
    });
  
    function makeConverter(){
      return new window.htmlToUdf({
        defaultFamily: defaultFamily.value || "Times New Roman",
        defaultSize: Number(defaultSize.value || 12),
      });
    }
  
    // --- HTML pane helpers ---
    if (htmlEditor) {
      let t = null;
      htmlEditor.addEventListener("input", () => {
        if (!htmlRender) return;
        clearTimeout(t);
        t = setTimeout(() => {
          try { htmlRender.innerHTML = makeConverter().normalize(htmlEditor.value || ""); } catch {}
        }, 120);
      });
    }
  
    if (btnApplyHtmlToVisual) {
      btnApplyHtmlToVisual.addEventListener("click", () => {
        try {
          const clean = makeConverter().normalize(htmlEditor.value || "");
          setEditorsFromNormalized(clean);
          activate("visual");
          buildPreviews();
        } catch (e) { console.error(e); }
      });
    }
  
    if (btnVisualToHtml) {
      btnVisualToHtml.addEventListener("click", () => {
        htmlEditor.value = visualEditor.innerHTML || "";
        if (htmlRender) {
          try { htmlRender.innerHTML = makeConverter().normalize(htmlEditor.value || ""); } catch {}
        }
        activate("html");
      });
    }
  
    // --- Text pane helpers ---
    if (btnVisualToText) {
      btnVisualToText.addEventListener("click", () => {
        if (textEditor) textEditor.value = htmlToText(visualEditor.innerHTML || "");
        activate("text");
      });
    }
  
    if (btnApplyTextToVisual) {
      btnApplyTextToVisual.addEventListener("click", () => {
        const html = textToHtml(textEditor ? textEditor.value : "");
        try {
          const clean = makeConverter().normalize(html);
          setEditorsFromNormalized(clean);
          activate("visual");
          buildPreviews();
        } catch (e) { console.error(e); }
      });
    }
  
    btnDownloadXml.addEventListener("click", () => {
      const res = buildPreviews();
      if (!res) return;
      const blob = new Blob([res.xml], { type: "application/xml;charset=utf-8" });
      downloadBlob(blob, `${fileName.value || "content"}.xml`);
      setStatus("ok", "XML indirildi.");
    });
  
    btnOpenBlob.addEventListener("click", () => {
      if (!lastUdfBlob) { setStatus("warn", "Henüz UDF üretilmedi."); return; }
      const url = lastUdfBlobUrl || URL.createObjectURL(lastUdfBlob);
      lastUdfBlobUrl = url;
      window.open(url, "_blank");
      setStatus("ok", "UDF linki yeni sekmede açıldı (Save As ile kaydet).");
    });
  
    btnDownloadUdf.addEventListener("click", async () => {
      const res = buildPreviews();
      if (!res) return;
  
      const blob = res.converter.getUdfBlob(res.clean);
      lastUdfBlob = blob;
      if (lastUdfBlobUrl) { try { URL.revokeObjectURL(lastUdfBlobUrl); } catch {} }
      lastUdfBlobUrl = downloadBlob(blob, `${fileName.value || "dilekce"}.udf`);
  
      try {
        const ab = await blob.arrayBuffer();
        const text = new TextDecoder("latin1").decode(new Uint8Array(ab));
        if (!text.includes("content.xml")) setStatus("warn", "UDF indirildi ama doğrulama zayıf (content.xml bulunamadı).");
        else setStatus("ok", "UDF indirildi.");
      } catch { setStatus("ok", "UDF indirildi."); }
    });
  
    function insertHtmlAtCursorIn(targetEl, html) {
      const sel = window.getSelection();
      if (!sel || !sel.rangeCount) { targetEl.insertAdjacentHTML("beforeend", html); return; }
      const range = sel.getRangeAt(0);
  
      // If selection isn't inside the target element, append to the end.
      let node = range.commonAncestorContainer;
      if (node && node.nodeType === 3) node = node.parentNode;
      if (!targetEl.contains(node)) { targetEl.insertAdjacentHTML("beforeend", html); return; }
  
      range.deleteContents();
      const temp = document.createElement("div");
      temp.innerHTML = html;
      const frag = document.createDocumentFragment();
      while (temp.firstChild) frag.appendChild(temp.firstChild);
      range.insertNode(frag);
      sel.collapseToEnd();
    }
  
    function insertHtmlAtCursor(html) {
      insertHtmlAtCursorIn(visualEditor, html);
    }
  
    visualEditor.addEventListener("paste", (e) => {
      if (!tabVisual.classList.contains("active")) return;
      const cd = e.clipboardData;
      if (!cd) return;
  
      const plainMode = chkPastePlain.checked;
      const html = cd.getData("text/html");
      const text = cd.getData("text/plain");
  
      e.preventDefault();
  
      if (plainMode || !html) {
        const safe = (text || "").replace(/\r\n/g, "\n");
        insertHtmlAtCursor(safe.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/\n/g,"<br>"));
        return;
      }
  
      try {
        const converter = new window.htmlToUdf({
          defaultFamily: defaultFamily.value || "Times New Roman",
          defaultSize: Number(defaultSize.value || 12),
        });
        const clean = converter.normalize(html);
        insertHtmlAtCursor(clean);
      } catch (err) {
        console.error(err);
        logError(err);
        insertHtmlAtCursor((text || "").replace(/\n/g, "<br>"));
      }
    });
  
    // HTML Önizleme alanına yapıştırma:
    // - Clipboard HTML varsa sanitize edip ekler
    // - Clipboard sadece düz metinse ama metin HTML'e benziyorsa (örn. <p>...</p>), HTML olarak yorumlayıp render eder
    // - Aksi halde düz metin ekler
    htmlRender?.addEventListener("paste", (e) => {
      if (!tabHtml.classList.contains("active")) return;
      const cd = e.clipboardData;
      if (!cd) return;
  
      const html = cd.getData("text/html");
      const text = (cd.getData("text/plain") || "");
  
      e.preventDefault();
  
      const conv = makeConverter();
  
      try {
        if (html) {
          const cleaned = conv.normalize(html);
          insertHtmlAtCursorIn(htmlRender, cleaned);
        } else if (text && /<\s*[a-z][\s\S]*>/i.test(text)) {
          // Plain text but looks like HTML code
          htmlRender.innerHTML = conv.normalize(text);
        } else if (text) {
          insertHtmlAtCursorIn(htmlRender, escapeHtml(text).replace(/\n/g, "<br>"));
        }
        // Sync source
        htmlEditor.value = conv.normalize(htmlRender.innerHTML || "");
      } catch (err) {
        console.error(err);
        logError(err);
      }
    });
  
    // Önizleme → HTML (render alanındaki düzenlemeyi kaynak textarea'ya yaz)
    btnPreviewToHtml?.addEventListener("click", () => {
      try {
        const conv = makeConverter();
        htmlEditor.value = conv.normalize(htmlRender?.innerHTML || "");
        setStatus("ok", "Önizleme → HTML aktarıldı.");
      } catch (err) {
        console.error(err);
        logError(err);
        setStatus("bad", "Önizleme → HTML hatası: " + (err?.message || err));
      }
    });
  
    btnSelfTest.addEventListener("click", async () => {
      try {
        setStatus("warn", "Self-test çalışıyor…");
  
        const conv = makeConverter();
  
        function extractContent(xml) {
          const m = xml.match(/<content>\s*<!\[CDATA\[([\s\S]*?)\]\]>\s*<\/content>/i);
          return m ? m[1] : "";
        }
  
        // 1) Bold/strong
        const boldHtml = `<p>aa <strong>bb</strong> cc</p>`;
        const boldXml = conv.getUdfXml(conv.normalize(boldHtml));
        if (!boldXml.includes('bold="true"')) throw new Error("Bold not detected.");
        const boldContent = extractContent(boldXml);
        if (!boldContent.includes("aa ") || !boldContent.includes("bb") || !boldContent.includes(" cc")) {
          throw new Error("Bold content missing.");
        }
  
  
        // 2) Mixed font size inside same paragraph
        const sizeHtml = `<p>aa <span style="font-size: 15pt">BB</span> cc</p>`;
        const sizeXml = conv.getUdfXml(conv.normalize(sizeHtml));
        if (!sizeXml.includes('size="15"')) throw new Error("Font size run missing (15pt).");
        const sizeContent = extractContent(sizeXml);
        if (!sizeContent.includes("BB")) throw new Error("Font size content missing.");
  
  // 2) List spacing + lower-alpha list-style-type
        const listHtml = `<ul>
  <li>asdasda</li>
  <li>asd</li>
  <li>as</li>
  <li>da</li>
  <li>sd</li>
  </ul>
  <ol>
  <li>dasdasd</li>
  <li>asd</li>
  <li>as</li>
  <li>d</li>
  <li>as</li>
  <li>d</li>
  <li>asd</li>
  </ol>
  <ol style="list-style-type: lower-alpha;">
  <li>asdaas</li>
  <li>da</li>
  <li>sd</li>
  <li>as</li>
  <li>d</li>
  <li>as</li>
  <li>da</li>
  <li>sd</li>
  <li>a</li>
  </ol>`;
        const listXml = conv.getUdfXml(conv.normalize(listHtml));
        const listContent = extractContent(listXml);
        if (!/sd\n\s*\n1\. dasdasd/.test(listContent)) throw new Error("Missing blank line between lists.");
        if (!listContent.includes("a. asdaas")) throw new Error("lower-alpha list markers missing.");
  
        // 3) Table basics
        const tableHtml = `<table>
  <tr><th>H</th><th>H2</th></tr>
  <tr><td>Cell 1</td><td>Cell 2</td></tr>
  </table>`;
        const tableXml = conv.getUdfXml(conv.normalize(tableHtml));
        if (!tableXml.includes("<table")) throw new Error("Table not converted.");
        if (!tableXml.includes("Cell 2")) throw new Error("Table text missing.");
  
        // 4) Zip package integrity
        const blob = conv.getUdfBlob(conv.normalize(boldHtml + listHtml + tableHtml));
        const entries = await parseZipEntries(blob);
  
        if (!entries.includes("content.xml")) throw new Error("Zip content.xml missing.");
  
        setStatus("ok", "Self-test OK ✅");
        showToast("Self-test OK", "ok");
      } catch (err) {
        console.error(err);
        setStatus("bad", "Self-test FAIL: " + (err?.message || err));
        showToast("Self-test FAIL: " + (err?.message || err), "bad");
      }
    });
  
    function init() {
      setBadge("ok", "JS hazır");
      setStatus("ok", "Hazır. Metin yaz veya yapıştır.");
      visualEditor.innerHTML = `<p style="text-align:center"><strong>UYAP UDF Dönüştürücü</strong></p><p>Buraya metin yaz veya HTML yapıştır.</p>`;
      buildPreviews();
    }
    init();
  })();
  